/**
 * HomeMapSection - Luxury Glass Portal Map Experience
 * 
 * Connected to real database listings with InfoBox cards
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, gql, compose } from 'react-apollo';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { GoogleMap, OverlayView, InfoBox } from "@react-google-maps/api";
import { MapPin, Navigation, Anchor, Search, Star, X, Maximize2, Minimize2 } from 'lucide-react';

import { googleMapAPI } from '../../../config';
import { loadGoogleMaps, isGoogleMapsLoaded } from '../../../helpers/googleMapsLoader';
import { formatURL } from '../../../helpers/formatURL';
import { getActiveCategory } from '../../../reducers/category';

// Clean Nautical Map Theme - Premium Style
const cleanNauticalStyles = [
  { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#a4ddf5" }] },
  { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 20 }] },
  { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }, { "lightness": 17 }] },
  { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#ffffff" }, { "lightness": 29 }, { "weight": 0.2 }] },
  { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 18 }] },
  { "featureType": "road.local", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 16 }] },
  { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 21 }] },
  { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#dedede" }, { "lightness": 21 }] },
  { "elementType": "labels.text.stroke", "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "lightness": 16 }] },
  { "elementType": "labels.text.fill", "stylers": [{ "saturation": 36 }, { "color": "#333333" }, { "lightness": 40 }] },
  { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
  { "featureType": "transit", "elementType": "geometry", "stylers": [{ "color": "#f2f2f2" }, { "lightness": 19 }] },
  { "featureType": "administrative", "elementType": "geometry.fill", "stylers": [{ "color": "#fefefe" }, { "lightness": 20 }] },
  { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#fefefe" }, { "lightness": 17 }, { "weight": 1.2 }] }
];

import s from './HomeMapSection.css';

// ============================================================================
// TODO: ADMIN PANEL CONFIGURATION
// These settings will be managed from SiteAdmin in the future
// ============================================================================
const ADMIN_CONFIG = {
  // Promotional texts (i18n ready)
  texts: {
    title: 'Find your freedom. Wherever you are.',
    subtitle: 'Discover extraordinary boats in the world\'s most beautiful destinations'
  },
  // Featured/Teleportation destinations (can be managed from admin)
  featuredDestinations: [
    { id: 'miami', name: 'Miami', lat: 25.7617, lng: -80.1918, zoom: 11 },
    { id: 'cancun', name: 'Cancún', lat: 21.1619, lng: -86.8515, zoom: 12 },
    { id: 'cartagena', name: 'Cartagena', lat: 10.3910, lng: -75.4794, zoom: 12 },
    { id: 'ibiza', name: 'Ibiza', lat: 38.9067, lng: 1.4206, zoom: 12 },
    { id: 'punta-cana', name: 'Punta Cana', lat: 18.5601, lng: -68.3725, zoom: 12 },
  ],
  // Behavior settings
  settings: {
    autoGeolocation: true,  // If false, always show global view
    defaultZoom: 10,
    maxZoom: 18,
    minZoom: 3
  }
};

// Default destinations as fallback (uses admin config)
const DEFAULT_DESTINATIONS = ADMIN_CONFIG.featuredDestinations.map(d => ({ ...d, count: 0 }));

// Helper function to get popular cities from listings
const getPopularCities = (listings) => {
  if (!listings || listings.length === 0) return DEFAULT_DESTINATIONS;

  // Group listings by city
  const cityMap = {};
  listings.forEach(listing => {
    // Try city first, then state, then country
    const cityName = listing.city || listing.state || listing.country;
    if (cityName && cityName.trim() !== '') {
      const key = cityName.trim().toLowerCase();
      if (!cityMap[key]) {
        cityMap[key] = {
          id: key.replace(/\s+/g, '-'),
          name: cityName.trim(),
          lat: listing.lat,
          lng: listing.lng,
          count: 0,
          zoom: 12
        };
      }
      cityMap[key].count++;
    }
  });

  // Convert to array and sort by count
  const cities = Object.values(cityMap)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Return cities or fallback to defaults
  return cities.length > 0 ? cities : DEFAULT_DESTINATIONS;
};

// Default center (Miami)
const DEFAULT_CENTER = { lat: 25.7617, lng: -80.1918 };
const DEFAULT_ZOOM = 10;

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Styled Components
const Section = styled(motion.section)`
  padding: 80px 0 !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  position: relative !important;
  overflow: hidden !important;
`;

const Container = styled.div`
  max-width: 1400px !important;
  margin: 0 auto !important;
  padding: 0 20px !important;
`;

const TitleWrapper = styled(motion.div)`
  text-align: center !important;
  margin-bottom: 48px !important;
`;

const Title = styled.h2`
  font-size: 48px !important;
  font-weight: 700 !important;
  margin: 0 0 16px 0 !important;
  background: linear-gradient(135deg, #0a1628 0%, #1e40af 50%, #3b82f6 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  font-family: 'Playfair Display', 'Georgia', serif !important;
  letter-spacing: -0.02em !important;
  
  @media (max-width: 768px) {
    font-size: 32px !important;
  }
`;

const Subtitle = styled.p`
  font-size: 18px !important;
  color: #64748b !important;
  margin: 0 !important;
  font-weight: 400 !important;
  
  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`;

const MapWrapper = styled(motion.div)`
  width: ${props => props.isExpanded ? '100vw' : '90%'} !important;
  max-width: ${props => props.isExpanded ? '100vw' : '1300px'} !important;
  height: ${props => props.isExpanded ? '100vh' : '600px'} !important;
  margin: 0 auto !important;
  position: ${props => props.isExpanded ? 'fixed' : 'relative'} !important;
  top: ${props => props.isExpanded ? '0' : 'auto'} !important;
  left: ${props => props.isExpanded ? '0' : 'auto'} !important;
  border-radius: ${props => props.isExpanded ? '0' : '24px'} !important;
  overflow: hidden !important;
  box-shadow: ${props => props.isExpanded ? 'none' : '0 20px 60px -10px rgba(0, 0, 0, 0.15)'} !important;
  border: ${props => props.isExpanded ? 'none' : '1px solid rgba(0, 0, 0, 0.05)'} !important;
  z-index: ${props => props.isExpanded ? '9999' : '1'} !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  @media (max-width: 768px) {
    width: ${props => props.isExpanded ? '100vw' : '95%'} !important;
    height: ${props => props.isExpanded ? '100vh' : '450px'} !important;
    border-radius: ${props => props.isExpanded ? '0' : '16px'} !important;
  }
`;

const GlassPanel = styled(motion.div)`
  position: absolute !important;
  bottom: 24px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  backdrop-filter: blur(40px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(40px) saturate(180%) !important;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0.6) 100%
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    inset 0 -1px 0 rgba(255, 255, 255, 0.3) !important;
  border-radius: 28px !important;
  padding: 18px 28px !important;
  min-width: 400px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  gap: 12px !important;
  z-index: 10 !important;
  
  @media (max-width: 768px) {
    width: 92% !important;
    min-width: unset !important;
    padding: 14px 18px !important;
    bottom: 16px !important;
    border-radius: 22px !important;
  }
`;

const LocationMessage = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  font-size: 14px !important;
  color: #334155 !important;
  font-weight: 500 !important;
  
  svg {
    color: #3b82f6 !important;
  }
`;

const DestinationButtons = styled.div`
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: center !important;
  gap: 8px !important;
`;

const PillButton = styled(motion.button)`
  padding: 10px 18px !important;
  border-radius: 50px !important;
  border: none !important;
  background: ${props => props.active
    ? 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)'
    : 'rgba(255, 255, 255, 0.8)'} !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  color: ${props => props.active ? '#ffffff' : '#1e293b'} !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  box-shadow: ${props => props.active
    ? '0 4px 14px rgba(10, 22, 40, 0.3)'
    : '0 2px 8px rgba(0, 0, 0, 0.08)'} !important;
  
  &:hover {
    transform: translateY(-2px) scale(1.02) !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
    background: ${props => props.active
    ? 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)'
    : 'rgba(255, 255, 255, 0.95)'} !important;
  }
  
  &:active {
    transform: translateY(0) scale(0.98) !important;
  }
`;

const MyLocationButton = styled(motion.button)`
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  width: 48px !important;
  height: 48px !important;
  border-radius: 50% !important;
  border: none !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(8px) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10 !important;
  color: #3b82f6 !important;
  
  &:hover {
    background: #ffffff !important;
    transform: scale(1.05) !important;
  }
`;

const ExpandButton = styled(motion.button)`
  position: absolute !important;
  top: 16px !important;
  right: 72px !important;
  width: 48px !important;
  height: 48px !important;
  border-radius: 50% !important;
  border: none !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(8px) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10 !important;
  color: #0a1628 !important;
  
  &:hover {
    background: #ffffff !important;
    transform: scale(1.05) !important;
    color: #3b82f6 !important;
  }
`;

const SearchAreaButton = styled(motion.button)`
  position: absolute !important;
  top: 16px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  padding: 12px 24px !important;
  border-radius: 50px !important;
  border: none !important;
  background: linear-gradient(135deg, #0a1628 0%, #1e40af 100%) !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
  z-index: 10 !important;
  
  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25) !important;
  }
`;

const ListingCount = styled.span`
  background: rgba(59, 130, 246, 0.15) !important;
  color: #3b82f6 !important;
  padding: 2px 10px !important;
  border-radius: 12px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
`;

// Custom Map Info Card - Full-Bleed Magazine Style
const MapInfoCard = styled.div`
  width: 300px !important;
  background: #ffffff !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28) !important;
  cursor: pointer !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
  padding: 0 !important;
  
  &:hover {
    transform: translateY(-4px) !important;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.32) !important;
  }
`;

const CardImage = styled.div`
  position: relative !important;
  width: 100% !important;
  height: 180px !important;
  overflow: hidden !important;
  border-top-left-radius: 12px !important;
  border-top-right-radius: 12px !important;
  margin: 0 !important;
  padding: 0 !important;
  
  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    transition: transform 0.5s ease !important;
    display: block !important;
  }
  
  &:hover img {
    transform: scale(1.05) !important;
  }
`;

const CardOverlay = styled.div`
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.4) 100%) !important;
  pointer-events: none !important;
`;

const CardCloseBtn = styled.button`
  position: absolute !important;
  top: 10px !important;
  right: 10px !important;
  width: 32px !important;
  height: 32px !important;
  border-radius: 50% !important;
  border: none !important;
  background: #ffffff !important;
  color: #0a1628 !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.2s ease !important;
  
  &:hover {
    transform: scale(1.1) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  }
`;

const CardPriceBadge = styled.div`
  position: absolute !important;
  bottom: 12px !important;
  left: 12px !important;
  background: #ffffff !important;
  padding: 6px 12px !important;
  border-radius: 8px !important;
  font-size: 18px !important;
  font-weight: 800 !important;
  color: #222222 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18) !important;
  
  span {
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #717171 !important;
  }
`;

const CardContent = styled.div`
  padding: 12px 14px 14px !important;
  background: #ffffff !important;
`;

const CardTitle = styled.h4`
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #222222 !important;
  margin: 0 0 8px 0 !important;
  line-height: 1.3 !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
`;

const CardMeta = styled.div`
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
`;

const CardRating = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #222222 !important;
  
  svg {
    color: #FF385C !important;
  }
`;

const CardType = styled.span`
  background: #F7F7F7 !important;
  padding: 6px 10px !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #717171 !important;
`;

const CardViewBtn = styled.span`
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  color: #222222 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  margin-top: 12px !important;
  padding: 10px 0 !important;
  border-top: 1px solid #EBEBEB !important;
  width: 100% !important;
  transition: color 0.2s ease !important;
  
  &:hover {
    color: #FF385C !important;
  }
`;

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: 'easeOut' }
  }
};

const titleVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.2 }
  }
};

const mapVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, delay: 0.4 }
  }
};

const glassVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.8 }
  }
};

class HomeMapSection extends Component {
  static propTypes = {
    locale: PropTypes.string,
    data: PropTypes.object,
    activeCategory: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      googleMapsLoaded: isGoogleMapsLoaded(),
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      locationMessage: 'Explore the world',
      activeDestination: null,
      userLocation: null,
      selectedListing: null,
      showSearchButton: false,
      mapBounds: null,
      isExpanded: false,  // Fullscreen mode
      hasTriedGeolocation: false  // Track if we've attempted geolocation
    };
    this.mapRef = null;
  }

  async componentDidMount() {
    // Load Google Maps using centralized helper
    if (!isGoogleMapsLoaded()) {
      try {
        await loadGoogleMaps();
        this.handleMapsLoaded();
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    } else {
      this.handleMapsLoaded();
    }
  }

  componentDidUpdate(prevProps) {
    // Refetch when activeCategory changes
    if (prevProps.activeCategory !== this.props.activeCategory) {
      if (this.props.data && this.props.data.refetch) {
        this.props.data.refetch({
          currentPage: 1,
          categoryType: this.props.activeCategory || 'boat'
        });
      }
    }
  }

  handleMapsLoaded = () => {
    this.setState({ googleMapsLoaded: true });
    // Try to get user location after maps is ready
    this.getUserLocation();
  };

  getUserLocation = () => {
    // Check if auto geolocation is enabled in admin config
    if (!ADMIN_CONFIG.settings.autoGeolocation) {
      this.setState({ hasTriedGeolocation: true });
      this.fitBoundsToAllBoats();
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.setState({
            userLocation,
            center: userLocation,
            zoom: 11,
            locationMessage: 'Boats near your location',
            hasTriedGeolocation: true
          });

          // Smooth pan to user location
          if (this.mapRef) {
            this.mapRef.panTo(userLocation);
            setTimeout(() => {
              this.mapRef.setZoom(11);
            }, 500);
          }
        },
        (error) => {
          // Geolocation denied or failed - activate Smart Bounds (God Mode)
          console.log('Geolocation denied/failed:', error.message);
          this.setState({
            locationMessage: 'Explore the world',
            hasTriedGeolocation: true
          });
          // Fit map to show all boats
          this.fitBoundsToAllBoats();
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    } else {
      // Navigator.geolocation not available - activate Smart Bounds
      this.setState({ hasTriedGeolocation: true });
      this.fitBoundsToAllBoats();
    }
  };

  // Smart Bounds - "God Mode" - Fit map to show all boats
  fitBoundsToAllBoats = () => {
    if (!this.mapRef || typeof window === 'undefined' || !window.google) return;

    const { data } = this.props;
    const listings = data?.SearchListing?.results || [];
    const validListings = listings.filter(l => l.lat && l.lng);

    if (validListings.length === 0) return;

    // Create bounds object
    const bounds = new window.google.maps.LatLngBounds();

    // Extend bounds for each listing
    validListings.forEach(listing => {
      bounds.extend({ lat: listing.lat, lng: listing.lng });
    });

    // Fit map to bounds with padding
    this.mapRef.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 100,
      left: 50
    });

    // Optional: limit max zoom after fitBounds
    setTimeout(() => {
      if (this.mapRef && this.mapRef.getZoom() > 14) {
        this.mapRef.setZoom(14);
      }
    }, 100);

    const categoryLabel = this.props.activeCategory === 'posada' ? 'inns' : this.props.activeCategory === 'experience' ? 'experiences' : 'boats';
    this.setState({ locationMessage: `Showing all ${validListings.length} ${categoryLabel} worldwide` });
  };

  // Toggle fullscreen mode
  toggleExpanded = () => {
    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded
    }), () => {
      // Trigger map resize after state change
      if (this.mapRef && window.google) {
        window.google.maps.event.trigger(this.mapRef, 'resize');
      }
    });
  };

  handleMapLoad = (map) => {
    this.mapRef = map;

    // If geolocation was already denied/failed, fit to all boats
    const { hasTriedGeolocation, userLocation } = this.state;
    if (hasTriedGeolocation && !userLocation) {
      setTimeout(() => this.fitBoundsToAllBoats(), 500);
    }
  };

  handleDestinationClick = (destination) => {
    this.setState({
      activeDestination: destination.id,
      locationMessage: `Exploring ${destination.name}`,
      selectedListing: null,
      showSearchButton: false
    });

    if (this.mapRef) {
      this.mapRef.panTo({ lat: destination.lat, lng: destination.lng });
      setTimeout(() => {
        this.mapRef.setZoom(destination.zoom);
      }, 300);
    }
  };

  handleMyLocationClick = () => {
    this.getUserLocation();
    this.setState({ activeDestination: null, selectedListing: null });
  };

  handleMarkerClick = (listing) => {
    this.setState({ selectedListing: listing });
  };

  handleInfoBoxClose = () => {
    this.setState({ selectedListing: null });
  };

  handleDragEnd = () => {
    if (this.mapRef) {
      const bounds = this.mapRef.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        this.setState({
          showSearchButton: true,
          mapBounds: {
            ne_lat: ne.lat(),
            ne_lng: ne.lng(),
            sw_lat: sw.lat(),
            sw_lng: sw.lng()
          }
        });
      }
    }
  };

  handleSearchArea = () => {
    const { mapBounds } = this.state;
    if (mapBounds && this.props.data && this.props.data.refetch) {
      this.props.data.refetch({
        currentPage: 1,
        sw_lat: mapBounds.sw_lat,
        sw_lng: mapBounds.sw_lng,
        ne_lat: mapBounds.ne_lat,
        ne_lng: mapBounds.ne_lng,
        searchByMap: true
      });
      this.setState({ showSearchButton: false });
    }
  };

  // Format price for display - Prioritize hourlyPrice over basePrice
  formatPrice = (listing) => {
    const hourlyPrice = listing.listingData?.hourlyPrice;
    const basePrice = listing.listingData?.basePrice || 0;
    const bookingMode = listing.listingData?.bookingMode;
    const currency = listing.listingData?.currency || 'USD';

    // Use hourlyPrice if available and booking mode supports it
    const price = (hourlyPrice && hourlyPrice > 0) ? hourlyPrice : basePrice;

    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      MXN: '$'
    };

    const symbol = symbols[currency] || '$';
    return `${symbol}${Math.round(price)}`;
  };

  // Get localized text based on user's language
  getLocalizedText = (settings, field) => {
    const { locale } = this.props;

    // Map locale to suffix
    const localeSuffixMap = {
      'en-US': '_en',
      'en': '_en',
      'fr-FR': '_fr',
      'fr': '_fr',
      'it-IT': '_it',
      'it': '_it',
      'pt-PT': '_pt',
      'pt': '_pt',
      'pt-BR': '_pt',
      'es': '', // Spanish is default
      'es-ES': ''
    };

    const suffix = localeSuffixMap[locale] || '';
    const localizedField = suffix ? `${field}${suffix}` : field;

    // Try localized version first, then fallback to Spanish (default)
    const localizedValue = settings?.[localizedField];
    if (localizedValue && localizedValue.trim()) {
      return localizedValue;
    }

    // Fallback to Spanish (base field)
    return settings?.[field] || ADMIN_CONFIG.texts[field];
  };

  // Get admin settings with fallbacks
  getAdminSettings = () => {
    const { mapSettings } = this.props;
    const settings = mapSettings?.getMapSectionSettings;

    // Parse destinations from JSON string
    let destinations = ADMIN_CONFIG.featuredDestinations;
    if (settings?.destinations) {
      try {
        destinations = JSON.parse(settings.destinations);
      } catch (e) {
        console.log('Error parsing destinations:', e);
      }
    }

    return {
      title: this.getLocalizedText(settings, 'title'),
      subtitle: this.getLocalizedText(settings, 'subtitle'),
      autoGeolocation: settings?.autoGeolocation !== false,
      defaultZoom: settings?.defaultZoom || ADMIN_CONFIG.settings.defaultZoom,
      destinations
    };
  };

  render() {
    const {
      googleMapsLoaded,
      center,
      zoom,
      locationMessage,
      activeDestination,
      selectedListing,
      showSearchButton,
      isExpanded
    } = this.state;

    const { data } = this.props;
    const listings = data && data.SearchListing && data.SearchListing.results ? data.SearchListing.results : [];
    const validListings = listings.filter(l => l.lat && l.lng);
    const popularCities = getPopularCities(validListings);

    // Get admin settings
    const adminSettings = this.getAdminSettings();

    return (
      <Section
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <Container>
          {/* Hide title when expanded */}
          {!isExpanded && (
            <TitleWrapper
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Title>{adminSettings.title}</Title>
              <Subtitle>{adminSettings.subtitle}</Subtitle>
            </TitleWrapper>
          )}

          <MapWrapper
            isExpanded={isExpanded}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {googleMapsLoaded && typeof google !== 'undefined' && google.maps ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={zoom}
                onLoad={this.handleMapLoad}
                onDragEnd={this.handleDragEnd}
                options={{
                  styles: cleanNauticalStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                  zoomControlOptions: {
                    position: (typeof google !== 'undefined' && google.maps && google.maps.ControlPosition)
                      ? google.maps.ControlPosition.RIGHT_CENTER
                      : 6
                  },
                  gestureHandling: 'cooperative'
                }}
              >
                {/* Price Markers using OverlayView */}
                {validListings.map(listing => (
                  <OverlayView
                    key={listing.id}
                    position={{ lat: listing.lat, lng: listing.lng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      className={`${s.priceMarker} ${selectedListing?.id === listing.id ? s.active : ''} ${listing.isFeatured ? s.featured : ''} ${listing.wishListStatus ? s.wishlisted : ''}`}
                      onClick={() => this.handleMarkerClick(listing)}
                    >
                      {listing.wishListStatus && <span className={s.heartIcon}>❤</span>}
                      {this.formatPrice(listing)}
                    </div>
                  </OverlayView>
                ))}

                {/* InfoBox for selected listing */}
                {selectedListing && selectedListing.id && googleMapsLoaded && typeof google !== 'undefined' && google.maps && (
                  <InfoBox
                    key={`infobox-${selectedListing.id}`}
                    position={{ lat: selectedListing.lat, lng: selectedListing.lng }}
                    options={{
                      closeBoxURL: '',
                      enableEventPropagation: true,
                      pixelOffset: new google.maps.Size(-150, -340),
                      boxStyle: {
                        width: '300px',
                        overflow: 'visible'
                      }
                    }}
                  >
                    <MapInfoCard key={`card-${selectedListing.id}`}>
                      <CardImage>
                        <a
                          href={`/boats/${formatURL(selectedListing.title)}-${selectedListing.id}`}
                        >
                          <img
                            src={selectedListing.listPhotos && selectedListing.listPhotos.length > 0
                              ? `/images/upload/x_medium_${(selectedListing.listPhotos.find(p => p.id === selectedListing.coverPhoto) || selectedListing.listPhotos[0])?.name?.replace(/\.(jpe?g|png)$/i, '.webp')}`
                              : '/images/placeholder.webp'
                            }
                            alt={selectedListing.title}
                          />
                        </a>
                        <CardOverlay />
                        <CardCloseBtn onClick={this.handleInfoBoxClose}>
                          <X size={16} />
                        </CardCloseBtn>
                        <CardPriceBadge>
                          {this.formatPrice(selectedListing)}
                          <span> /day</span>
                        </CardPriceBadge>
                      </CardImage>
                      <CardContent>
                        <CardTitle>{selectedListing.title}</CardTitle>
                        <CardMeta>
                          <CardRating>
                            {selectedListing.reviewsStarRating > 0 ? (
                              <>
                                <Star size={14} fill="#f59e0b" />
                                <span>{selectedListing.reviewsStarRating}</span>
                              </>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>New</span>
                            )}
                          </CardRating>
                          <CardType>
                            {selectedListing.settingsData?.[0]?.listsettings?.itemName || 'Boat'}
                          </CardType>
                        </CardMeta>
                        <a
                          href={`/boats/${formatURL(selectedListing.title)}-${selectedListing.id}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <CardViewBtn>
                            View details
                            <span>→</span>
                          </CardViewBtn>
                        </a>
                      </CardContent>
                    </MapInfoCard>
                  </InfoBox>
                )}
              </GoogleMap>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'linear-gradient(135deg, #e8f4f8 0%, #d0e8f0 100%)' }}>
                <span style={{ color: '#666', fontSize: '16px' }}>Loading map...</span>
              </div>
            )}

            {/* Search in this area button */}
            {showSearchButton && (
              <SearchAreaButton
                onClick={this.handleSearchArea}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Search size={16} />
                Search this area
              </SearchAreaButton>
            )}

            {/* Expand/Minimize Button */}
            <ExpandButton
              onClick={this.toggleExpanded}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={isExpanded ? "Exit fullscreen" : "Fullscreen map"}
            >
              {isExpanded ? <Minimize2 size={22} /> : <Maximize2 size={22} />}
            </ExpandButton>

            {/* My Location Button */}
            <MyLocationButton
              onClick={this.handleMyLocationClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Go to my location"
            >
              <Navigation size={22} />
            </MyLocationButton>

            {/* Glass Control Panel */}
            <GlassPanel
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <LocationMessage>
                <MapPin size={18} />
                <span>{locationMessage}</span>
                {validListings.length > 0 && (
                  <ListingCount>{validListings.length} {this.props.activeCategory === 'posada' ? 'inns' : this.props.activeCategory === 'experience' ? 'experiences' : 'boats'}</ListingCount>
                )}
              </LocationMessage>

              <DestinationButtons>
                {popularCities.length > 0 ? (
                  popularCities.map(city => (
                    <PillButton
                      key={city.id}
                      active={activeDestination === city.id}
                      onClick={() => this.handleDestinationClick(city)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Anchor size={14} />
                      {city.name}
                      <span style={{
                        fontSize: '11px',
                        opacity: 0.7,
                        marginLeft: '4px'
                      }}>
                        ({city.count})
                      </span>
                    </PillButton>
                  ))
                ) : (
                  <span style={{ fontSize: '14px', color: '#64748b' }}>
                    Loading destinations...
                  </span>
                )}
              </DestinationButtons>
            </GlassPanel>
          </MapWrapper>
        </Container>
      </Section>
    );
  }
}

const mapState = (state) => ({
  locale: state.intl.locale,
  activeCategory: getActiveCategory(state)
});

export default compose(
  withStyles(s),
  connect(mapState),
  graphql(gql`
    query getMapSectionSettings {
      getMapSectionSettings {
        id
        title
        title_en
        title_fr
        title_it
        title_pt
        subtitle
        subtitle_en
        subtitle_fr
        subtitle_it
        subtitle_pt
        autoGeolocation
        defaultZoom
        destinations
      }
    }
  `, {
    name: 'mapSettings',
    options: {
      fetchPolicy: 'cache-and-network'
    }
  }),
  graphql(gql`
    query SearchListing($currentPage: Int, $lat: Float, $lng: Float, $sw_lat: Float, $sw_lng: Float, $ne_lat: Float, $ne_lng: Float, $searchByMap: Boolean, $categoryType: String) {
      SearchListing(currentPage: $currentPage, lat: $lat, lng: $lng, sw_lat: $sw_lat, sw_lng: $sw_lng, ne_lat: $ne_lat, ne_lng: $ne_lng, searchByMap: $searchByMap, categoryType: $categoryType) {
        count
        results {
          id
          title
          personCapacity
          lat
          lng
          beds
          bookingType
          coverPhoto
          reviewsCount
          reviewsStarRating
          transmission
          city
          state
          country
          categoryType
          listPhotos {
            id
            name
            type
            status
          }
          listingData {
            basePrice
            hourlyPrice
            currency
            bookingMode
          }
          settingsData {
            listsettings {
              id
              itemName
            }
          }
          wishListStatus
          isListOwner
          isFeatured
        }
      }
    }
  `, {
    options: (props) => ({
      variables: {
        currentPage: 1,
        categoryType: props.activeCategory || 'boat'
      },
      fetchPolicy: 'network-only'
    })
  })
)(HomeMapSection);
